import { cleanText, cleanValue } from "../lib/sanitize";
import type { ActivityType } from "./types";

export interface ErrorsApi {
    track: (type: ActivityType, data: Record<string, unknown>) => void;
    /** id текущего сеанса журнала — лимит ошибок считается на сеанс */
    sessionRoot: () => string | null;
    now?: () => number;
}

const MAX_PER_SESSION = 20;
const REPEAT_MS = 60_000;

const basename = (src: string): string => {
    const clean = src.split(/[?#]/)[0];
    return cleanValue(clean.slice(clean.lastIndexOf("/") + 1), 80);
};

export const installErrors = (api: ErrorsApi): (() => void) => {
    const now = api.now || Date.now;
    let root: string | null = null;
    let count = 0;
    const recent = new Map<string, number>();

    const report = (
        kind: "error" | "unhandledrejection",
        message: string,
        extra: { source?: string; line?: number; col?: number; stack?: string },
    ) => {
        const r = api.sessionRoot();
        if (r !== root) {
            root = r;
            count = 0;
            recent.clear();
        }
        if (count >= MAX_PER_SESSION) return;
        const msg = cleanText(message, 200) || "(no message)";
        const t = now();
        const last = recent.get(msg);
        if (last !== undefined && t - last < REPEAT_MS) return;
        recent.set(msg, t);
        count++;
        api.track("error", {
            kind,
            message: msg,
            source: extra.source ? basename(extra.source) : undefined,
            line: extra.line || undefined,
            col: extra.col || undefined,
            stack: extra.stack ? cleanText(extra.stack, 500) : undefined,
        });
    };

    const onError = (e: Event) => {
        if (!(e instanceof ErrorEvent)) return;
        try {
            const err = e.error as { stack?: unknown } | null;
            report("error", e.message, {
                source: e.filename,
                line: e.lineno,
                col: e.colno,
                stack: typeof err?.stack === "string" ? err.stack : undefined,
            });
        } catch {
            /* обработчик ошибок не должен порождать новые */
        }
    };

    const onRejection = (e: PromiseRejectionEvent) => {
        try {
            const r = e.reason as unknown;
            const message = r instanceof Error ? r.message : String(r);
            report("unhandledrejection", message, {
                stack: r instanceof Error && typeof r.stack === "string" ? r.stack : undefined,
            });
        } catch {
            /* ignore */
        }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
        window.removeEventListener("error", onError);
        window.removeEventListener("unhandledrejection", onRejection);
    };
};
